import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { REQUEST } from '@angular/core';
import { environment } from '../../../environments/environment';
import { PermissionsService } from '../permissions.service';
import { BillingService } from '../billing.service';

/** Request-like: has headers.get for Cookie header (from server.ts adapter) */
type RequestLike = { headers?: { get?: (n: string) => string | null } };

type LoginRequest = { email: string; password: string; rememberMe?: boolean };
export type CompactProfile = {
  id: number;
  org_name: string;
  org_slug: string;
  display_name: string;
  avatar: string | null;
  unread_count?: number;
  roles?: string[];
  permissions?: string[];
  is_support_profile?: boolean;
  support_role?: 'agent' | 'support_supervisor' | 'system_manager' | 'platform_manager' | 'platform_owner';
  /** Grant profile: support agent with temp admin access to customer org */
  is_support_grant?: boolean;
  support_profile_id?: number;
};
type LoginResponse = { access: string; refresh: string; user_id: number; permissions?: Record<string, boolean>; profiles: CompactProfile[] };

@Injectable({
  providedIn: 'root'
})
export class Auth {
  private http = inject(HttpClient);
  private router = inject(Router);
  private permissionsService = inject(PermissionsService);
  private billingService = inject(BillingService);
  private platformId = inject(PLATFORM_ID);
  private ssrRequest = inject(REQUEST, { optional: true }) as RequestLike | null;

  // Cookie keys
  private userCookieKey = 'rp_user';
  private accessCookieKey = 'rp_access';
  private refreshCookieKey = 'rp_refresh';
  private profileCookieKey = 'rp_profile';
  private profilesSessionKey = 'rp_profiles';

  private lastProfiles: CompactProfile[] | null = null;
  private cookieDays = 30; // Used for setSelectedProfile after login
  private readonly profilesUrl = `${environment.apiBaseUrl}/identity/profiles/`;

  // Session helpers
  isLoggedIn(): boolean {
    return this.getCookie(this.userCookieKey) !== null;
  }

  getAccessToken(): string | null {
    return this.getCookie(this.accessCookieKey);
  }

  getSelectedProfileId(): number | null {
    const val = this.getCookie(this.profileCookieKey);
    return val ? Number(val) : null;
  }

  isSupportProfile(): boolean {
    const p = this.getSelectedProfile();
    return !!(p?.is_support_profile);
  }

  hasBillingToolsAccess(): boolean {
    const role = this.getSelectedProfile()?.support_role;
    return role === 'support_supervisor' || role === 'system_manager' || role === 'platform_manager' || role === 'platform_owner';
  }

  hasSystemToolsAccess(): boolean {
    const role = this.getSelectedProfile()?.support_role;
    return role === 'system_manager' || role === 'platform_manager' || role === 'platform_owner';
  }

  hasPlatformAdminAccess(): boolean {
    const role = this.getSelectedProfile()?.support_role;
    return role === 'platform_manager' || role === 'platform_owner';
  }

  /** True when viewing as support agent with temp admin access to a customer org */
  isSupportGrantContext(): boolean {
    const p = this.getSelectedProfile();
    return !!(p?.is_support_grant);
  }

  isPlatformAdmin(): boolean {
    return this.hasPlatformAdminAccess();
  }

  getSelectedProfile(): CompactProfile | null {
    const profileId = this.getSelectedProfileId();
    if (!profileId) return null;

    const profiles = this.getProfiles();
    return profiles.find(p => p.id === profileId) || null;
  }

  /**
   * Hydrate permissions from cached profile (sessionStorage) on refresh.
   * Called by permission guards so they don't redirect to /notifications before
   * async fetchProfilesFromApi() completes.
   */
  ensurePermissionsFromCache(): void {
    const selected = this.getSelectedProfile();
    if (selected?.permissions?.length) {
      this.permissionsService.set(selected.permissions);
    }
  }

  /** Store tokens from signup (instant activation). */
  async handleSignupTokens(access: string, refresh: string, userId: number, rememberMe: boolean = true): Promise<void> {
    const days = rememberMe ? 30 : 0;
    this.cookieDays = days;
    this.setCookie(this.userCookieKey, String(userId), days);
    this.setCookie(this.accessCookieKey, access, days);
    this.setCookie(this.refreshCookieKey, refresh, days);
  }

  async login(credentials: LoginRequest, rememberMe: boolean = true): Promise<CompactProfile[] | null> {
    const url = `${environment.apiBaseUrl}/identity/auth/login/`;
    const { email, password } = credentials;
    const resp = await this.http.post<LoginResponse>(url, { email, password }).toPromise();
    if (!resp) throw new Error('No response from server');
    // Remember me: 30 days; otherwise session cookie (until browser close)
    const days = rememberMe ? 30 : 0;
    this.cookieDays = days;
    this.setCookie(this.userCookieKey, String(resp.user_id), days);
    this.setCookie(this.accessCookieKey, resp.access, days);
    this.setCookie(this.refreshCookieKey, resp.refresh, days);
    // Store profiles for selection screen
    this.lastProfiles = resp.profiles ?? null;
    try { sessionStorage.setItem(this.profilesSessionKey, JSON.stringify(this.lastProfiles || [])); } catch {}
    // Set permissions (from first profile for single-profile users; multi-profile set on choose)
    if (resp.permissions && typeof resp.permissions === 'object') {
      this.permissionsService.set(Object.keys(resp.permissions));
    } else if (this.lastProfiles?.length === 1 && this.lastProfiles[0].permissions) {
      this.permissionsService.set(this.lastProfiles[0].permissions);
    }
    return this.lastProfiles;
  }

  logout(): void {
    this.deleteCookie(this.userCookieKey);
    this.deleteCookie(this.accessCookieKey);
    this.deleteCookie(this.refreshCookieKey);
    this.deleteCookie(this.profileCookieKey);
    try { sessionStorage.removeItem(this.profilesSessionKey); } catch {}
    this.lastProfiles = null;
    this.permissionsService.set({});
    // Don't let a suspended org's lock state leak into the next login.
    this.billingService.resetLockState();
    this.router.navigateByUrl('/auth/login');
  }

  setSelectedProfile(profileId: number): void {
    this.setCookie(this.profileCookieKey, String(profileId), this.cookieDays);
    // The billing lock signals/cache describe the previous profile's org; a
    // suspended org's stale hardLocked flag must not follow the user into a
    // healthy org (it would bounce them back to /suspended).
    this.billingService.resetLockState();
  }

  getProfiles(): CompactProfile[] {
    if (this.lastProfiles) return this.lastProfiles;
    try {
      const raw = sessionStorage.getItem(this.profilesSessionKey);
      if (!raw) return [];
      const list = JSON.parse(raw);
      if (Array.isArray(list)) return list as CompactProfile[];
      return [];
    } catch {
      return [];
    }
  }

  async fetchProfilesFromApi(): Promise<CompactProfile[]> {
    const result = await this.http.get<{profiles: CompactProfile[]}>(this.profilesUrl).toPromise();
    const profiles = result?.profiles ?? [];
    this.lastProfiles = profiles;
    try { sessionStorage.setItem(this.profilesSessionKey, JSON.stringify(profiles)); } catch {}
    // Keep permissions in sync with selected profile
    const selectedId = this.getSelectedProfileId();
    const selected = profiles.find(p => p.id === selectedId);
    if (selected?.permissions?.length) {
      this.permissionsService.set(selected.permissions);
    }
    return profiles;
  }

  // Basic cookie utilities. days=0 means session cookie (no expires).
  private setCookie(name: string, value: string, days: number) {
    if (typeof document === 'undefined') return;
    const base = `${name}=${encodeURIComponent(value)}; path=/`;
    const cookie = days > 0
      ? `${base}; expires=${new Date(Date.now() + days * 86400000).toUTCString()}`
      : base;
    document.cookie = cookie;
  }

  private getCookie(name: string): string | null {
    if (isPlatformBrowser(this.platformId)) {
      if (typeof document === 'undefined') return null;
      const cookies = document.cookie.split('; ');
      for (const cookie of cookies) {
        const [key, ...rest] = cookie.split('=');
        if (key === name) {
          return decodeURIComponent(rest.join('=').trim());
        }
      }
      return null;
    }
    // SSR: read from incoming request Cookie header (avoids login flash on reload)
    const cookieHeader = this.ssrRequest?.headers?.get?.('cookie');
    if (!cookieHeader) return null;
    const cookies = cookieHeader.split('; ');
    for (const cookie of cookies) {
      const [key, ...rest] = cookie.split('=');
      if (key?.trim() === name) {
        return decodeURIComponent((rest.join('=') || '').trim());
      }
    }
    return null;
  }

  private deleteCookie(name: string) {
    if (typeof document === 'undefined') return;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
  }
}
